import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { FinanceiroService } from '../../../../../services/financeiro.service';
import { BaixarParcelaCartaoDTO, ContaAReceberVendaItemDTO, VendaDetalheDTO } from '../../../../../models/financeiro.model';
import { VendaModalV2 } from '../../../../modal/venda-modal-v2/venda-modal-v2';
import { ConfirmDialogComponent } from '../../../../modal/confirm-dialog/confirm-dialog';
import { AuthService } from '../../../../../services/auth.service';
import { formatarMoedaBRL } from '../../../../../core/utils/moeda.util';

@Component({
  selector: 'app-contas-a-receber-manutencao.component',
  imports: [CommonModule, VendaModalV2, ConfirmDialogComponent],
  templateUrl: './contas-a-receber-manutencao.component.html',
  styleUrl: './contas-a-receber-manutencao.component.css',
})
export class ContasAReceberManutencaoComponent implements OnInit {
  idVenda: number | null = null;
  venda: VendaDetalheDTO | null = null;
  contasReceberVenda: ContaAReceberVendaItemDTO[] = [];
  isLoading = false;
  isBodyLoading = false;
  errorMessage = '';
  bodyErrorMessage = '';
  isModalBaixarParcelaOpen = false;
  isConfirmDialogOpen = false;
  selectedItem: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private financeiroService: FinanceiroService,
    private toastr: ToastrService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    this.idVenda = id ? Number(id) : null;

    if (!this.idVenda || Number.isNaN(this.idVenda) || this.idVenda <= 0) {
      this.errorMessage = 'ID da venda invalido para consulta.';
      return;
    }

    this.carregarVenda(this.idVenda);
    this.carregarContasReceberBody(this.idVenda);
  }

  private carregarVenda(idVenda: number): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.financeiroService.getVendaById(idVenda).subscribe({
      next: (response) => {
        this.venda = response;
        this.isLoading = false;
      },
      error: () => {
        this.errorMessage = 'Nao foi possivel carregar os dados da venda.';
        this.isLoading = false;
      },
    });
  }

  private carregarContasReceberBody(idVenda: number): void {
    this.isBodyLoading = true;
    this.bodyErrorMessage = '';

    this.financeiroService.getContasAReceberByVenda(idVenda).subscribe({
      next: (response: ContaAReceberVendaItemDTO[]) => {
        this.contasReceberVenda = response ?? [];
        this.isBodyLoading = false;
      },
      error: () => {
        this.bodyErrorMessage = 'Nao foi possivel carregar as parcelas de contas a receber desta venda.';
        this.isBodyLoading = false;
      },
    });
  }

  /**
   * Quanto ainda pode ser lançado nesta venda.
   *
   * **Não é o saldo devedor.** Formas parceladas geram parcelas sem abatê-lo —
   * o saldo só cai quando cada parcela é recebida. Validar pelo saldo
   * permitiria lançar o mesmo valor repetidamente e comprometer mais que o
   * total da venda (venda de 1000 chegando a 1500 em parcelas).
   *
   * Os status de `contas_a_receber` são apenas ABERTO, PARCIAL e LIQUIDADO —
   * não há cancelamento, então a soma simples é correta.
   *
   * Ver docs/planos/2026-09-25-plano-correcao-super-comprometimento-venda.md
   */
  get valorDisponivelParaLancamento(): number | null {
    if (!this.venda) return null;
    const comprometido = this.contasReceberVenda.reduce(
      (total, parcela) => total + (parcela.valorPrevisto ?? 0), 0
    );
    return Math.max(0, this.venda.totalVenda - comprometido);
  }

  formatCurrency(value: number | null | undefined): string {
    return formatarMoedaBRL(value ?? 0);
  }

  formatDate(value: string | null | undefined, includeTime = false): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }

    const options: Intl.DateTimeFormatOptions = includeTime
      ? {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }
      : {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        };

    return date.toLocaleString('pt-BR', options);
  }

  baixarParcelaCartao(item: any): void {
    this.isConfirmDialogOpen = true;
    this.selectedItem = item;
  }

  confirmarBaixaParcelaCartao(): void {
    
    const payload: BaixarParcelaCartaoDTO = {
      idVenda: this.venda?.id || 0,
      numParcela: this.selectedItem.numParcela,
      valorRecebido: this.selectedItem.valorPrevisto,
      idUsuario: this.authService.getUser()?.id || 0
    };

    this.financeiroService.baixarParcelaCartao(payload).subscribe({
      next: () => {
        this.toastr.success('Parcela baixada com sucesso.');
        this.ngOnInit(); // Recarrega os dados para refletir a alteração

      },
      error: () => {
        this.toastr.error('Não foi possível baixar a parcela.');
      }
    });



    this.fecharDialogConfirmacao();
  }

  fecharDialogConfirmacao(): void {
    
    this.isConfirmDialogOpen = false;
  }

  abrirModalBaixarParcela(): void {
    this.isModalBaixarParcelaOpen = true;
  }

  fecharModalBaixarParcela(concluido: boolean = false): void {
    this.isModalBaixarParcelaOpen = false;

    if (concluido && this.idVenda) {
      // Recarrega os dados para refletir a alteração
      this.carregarVenda(this.idVenda);
      this.carregarContasReceberBody(this.idVenda);
    }
  }

  get somaValorPrevistoParcelas(): number {
    return this.contasReceberVenda.reduce((total, item) => total + (item.valorPrevisto ?? 0), 0);
  }

  get podeExibirAdicionarPagamento(): boolean {
    if (!this.venda) {
      return false;
    }

    const isVendaMista = this.venda.formaPagamento === 'VENDA MISTA';
    return isVendaMista && (this.somaValorPrevistoParcelas < (this.venda.saldoDevedor ?? 0) || this.venda.saldoDevedor > 0) ;
  }

  voltarParaContasAReceber(): void {
    this.router.navigate(['/home', 'financeiro-conta-a-receber']);
  }

}
